import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "@/components/site/primitives";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { IMAGES } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Guide = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/guide-subscribe`, { email, lang });
      toast.success(t.guide.success);
      setEmail("");
      if (data?.pdf_url) {
        setPdfUrl(data.pdf_url);
        window.open(data.pdf_url, "_blank", "noopener");
      }
    } catch (err) {
      toast.error(t.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="guide" className="relative py-28 sm:py-36 bg-espresso/30 border-y border-gold/10" data-testid="guide-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.8fr_1fr] gap-12 lg:gap-16 items-center">
        {/* cover */}
        <Reveal>
          <div className="relative mx-auto max-w-xs">
            <div className="relative overflow-hidden rounded-2xl border border-gold/30 aspect-[3/4] shadow-[0_30px_70px_-25px_rgba(0,0,0,0.7)]">
              <img src={IMAGES.cards2} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
              <div className="absolute inset-0 bg-gradient-to-b from-espresso/70 to-ivy/90" />
              <div className="relative h-full flex flex-col items-center justify-center p-8 text-center">
                <span className="text-gold text-3xl">☾</span>
                <p className="mt-6 font-serifluxe text-gold text-xs tracking-mystic uppercase">Ask Monia</p>
                <p className="mt-3 font-editorial italic text-sand text-2xl leading-tight">{t.guide.title}</p>
                <p className="mt-6 text-sand/50 text-[11px] tracking-widest uppercase">PDF · Gratis</p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* form */}
        <Reveal delay={0.1}>
          <Eyebrow>{t.guide.eyebrow}</Eyebrow>
          <h2 className="mt-5 font-display font-light text-sand text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            {t.guide.title}
          </h2>
          <p className="mt-6 text-sand/70 text-base sm:text-lg leading-relaxed font-light max-w-lg">{t.guide.body}</p>
          <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.guide.placeholder}
              className="h-12 bg-ivy/60 border-gold/25 text-sand placeholder:text-sand/40 focus-visible:ring-gold rounded-full px-5"
              data-testid="guide-email-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="h-12 rounded-full bg-rust hover:bg-rust/90 text-cream px-7 font-body tracking-wide whitespace-nowrap transition-transform hover:scale-[0.98]"
              data-testid="guide-submit-button"
            >
              {t.guide.cta}
            </Button>
          </form>
          <p className="mt-3 text-sand/40 text-xs">{t.guide.note}</p>
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 text-gold hover:text-cream transition-colors font-body text-sm tracking-wide underline underline-offset-4 decoration-gold/40"
              data-testid="guide-download-link"
            >
              ☾ {t.guide.download}
            </a>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default Guide;
